<template>
  <div class="attendance-tracking">
    <!-- Awards Menu -->
    <AwardsMenu
      :visible="awardsMenuVisible"
      :student="selectedStudent"
      @award-selected="handleAwardSelected"
      @award-removed="handleAwardRemoved"
      @close="closeAwardsMenu"
    />

    <div class="attendance">
      <b-loading :is-full-page="false" v-model="isLoading"></b-loading>

      <div class="attendance__header" v-if="topic">
        <h1 class="has-text-weight-bold">{{ schoolName }}</h1>
        <h2>{{ displayDateTime }}</h2>
      </div>

      <div class="attendance__body" v-if="topic">
        <table style="width: 100%" cellspacing="1">
          <thead>
            <tr>
              <!-- <th></th>
              <th>ID</th> -->
              <th>Student</th>
              <th></th>
              <th>Score</th>
              <th>Attend</th>
              <th>Behavior</th>
            </tr>
          </thead>
          <tbody>
            <!-- Main line moves -->
            <tr v-for="(student, index) in students" :key="`student-${index}`">
              <!-- <td class="student-cell">
                {{ index + 1 }}
              </td> -->

              <td
                class="student-cell"
                style="min-width: 100px"
                @contextmenu.prevent="showAwardsMenu(student)"
              >
                <div class="student-name-container">
                  <div v-if="student.nthPaymentRemainder > 0">
                    <b-tooltip
                      :label="getPaymentTooltip(student)"
                      position="is-right"
                      type="is-danger"
                    >
                      <b-tag rounded type="is-danger">
                        {{ student.nthPaymentRemainder }}
                      </b-tag>
                    </b-tooltip>
                  </div>

                  <span class="student-name">
                    {{ student.firstName }} {{ student.lastName }}
                  </span>
                  <RollAwardMini :awards="student.awards || []" />
                </div>
              </td>

              <td class="student-cell">
                <LectureLeaderboardScore :student="student" />
              </td>

              <td class="student-cell" style="">
                <div class="is-flex">
                  <input
                    style="width: 2rem; text-align: center"
                    v-model="student.score"
                    readonly
                  />
                  <div class="is-flex is-flex-direction-column">
                    <span class="score-button" @click="student.score++">+</span>
                    <span class="score-button" @click="student.score--">−</span>
                  </div>
                </div>
              </td>

              <td class="student-cell">
                <div style="margin-left: 0.5em; margin-top: 0.25em">
                  <b-switch
                    v-model="student.attend"
                    type="is-success"
                    size="is-small"
                    @input="
                      (val) => {
                        handleAttendChanged(student, val);
                      }
                    "
                  ></b-switch>
                </div>
              </td>

              <td class="student-cell">
                <BehaviorSelect
                  :initial-behavior="student.behavior"
                  @onChanged="
                    (value) => {
                      student.behavior = value;
                    }
                  "
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="attendance__footer" v-if="students.length">
        <div class="footer-buttons">
          <!-- Left side placeholder for future content -->
          <div style="min-width: 40px"></div>

          <b-button
            type="is-primary is-light"
            outlined
            icon-left="save"
            @click="handleSaveAll"
            label="Save"
          >
          </b-button>

          <b-button
            type="is-info is-light"
            outlined
            icon-left="trophy"
            @click="openLeaderboard"
          >
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import SACFrontendService from "@/services/SACFrontendService";
import BehaviorSelect from "@/components/teacher/BehaviorSelect.vue";
import RollAwardMini from "@/components/teacher/RollAwardMini.vue";
import AwardsMenu from "@/components/teacher/AwardsMenu.vue";
import { formatDate } from "vue-chess-shared/utils/date-helper";
import LectureLeaderboardScore from "@/components/shared/LectureLeaderboardScore.vue";

export default {
  name: "ClassRoll",

  components: {
    BehaviorSelect,
    RollAwardMini,
    AwardsMenu,
    LectureLeaderboardScore,
  },

  data() {
    return {
      isLoading: false,
      awardsMenuVisible: false,
      selectedStudent: null,
    };
  },

  computed: {
    ...mapGetters({
      currentClass: "getCurrentClass",
      currentLecture: "getCurrentLecture",
      leaderboardModalOpen: "getLeaderboardModalOpen",
    }),

    schoolName() {
      return this.currentClass.schoolName + " | " + this.currentClass.groupName;
    },

    topic() {
      return this.currentClass.topic;
    },

    students() {
      const students = this.currentClass.students ?? [];
      // Sort students so that nthPaymentRemainder > 0 goes to the bottom
      return [...students].sort((a, b) => {
        return (a.nthPaymentRemainder || 0) - (b.nthPaymentRemainder || 0);
      });
    },

    displayDateTime() {
      if (!this.currentLecture) {
        return "";
      }

      return `${formatDate(
        this.currentLecture.lectureDate,
        "DD/MM/YYYY dddd"
      )} ${formatDate(
        this.currentLecture.lectureDate,
        "hh:mm A"
      )} - ${formatDate(this.currentLecture.lectureEndDate, "hh:mm A")}`;
    },
  },

  methods: {
    ...mapActions({
      updateLeaderboardModalOpen: "updateLeaderboardModalOpen",
    }),

    getPaymentTooltip(student) {
      const paymentReminderText = `Payment Reminder: ${student.nthPaymentRemainder}`;

      const termPaidStatusMap = {
        2: "Full",
        1: "Partial - owing",
        3: "Partial - approved",
        0: "None",
        4: "Unable to chase",
        5: "Refusal",
      };

      const termPaidStatusText = `Term Paid Status: ${
        termPaidStatusMap[student.termPaidStatus] || student.termPaidStatus
      }`;

      return `${paymentReminderText} | ${termPaidStatusText}`;
    },

    openLeaderboard() {
      this.updateLeaderboardModalOpen(true);
    },

    showAwardsMenu(student) {
      this.selectedStudent = student;
      this.awardsMenuVisible = true;
    },

    closeAwardsMenu() {
      this.awardsMenuVisible = false;
      this.selectedStudent = null;
    },

    handleAwardSelected({ award, student }) {
      if (!student.awards) {
        this.$set(student, "awards", []);
      }

      if (!student.awards.includes(award.code)) {
        const newAwards = [...student.awards, award.code];
        this.$set(student, "awards", newAwards);

        // Show success toast
        this.$buefy.toast.open({
          message: `Added "${award.name}" to ${student.firstName} ${student.lastName}`,
          type: "is-success",
          duration: 3000,
        });
      }
    },

    handleAwardRemoved({ award, student }) {
      if (student.awards && student.awards.includes(award.code)) {
        const newAwards = student.awards.filter((code) => code !== award.code);
        this.$set(student, "awards", newAwards);

        // Show removal toast
        this.$buefy.toast.open({
          message: `Removed "${award.name}" from ${student.firstName} ${student.lastName}`,
          type: "is-warning",
          duration: 3000,
        });
      }
    },

    async handleSaveAll() {
      const lectureId = this.$route.params.lectureId;
      if (!lectureId) {
        this.$buefy.toast.open({
          message: `Lecture ID is not available!`,
          type: "is-danger",
        });
        return;
      }

      this.isLoading = true;
      try {
        this.$buefy.dialog.confirm({
          title: "Confirmation",
          message: `Are you sure to submit all?`,
          cancelText: "No",
          confirmText: "Yes",
          type: "is-info",
          trapFocus: true,
          onConfirm: async () => {
            await SACFrontendService.updateStudentList(
              this.year,
              this.term,
              lectureId,
              this.currentClass.classId,
              this.currentClass.topic,
              this.currentClass.students
            );
            this.$buefy.toast.open({
              message: `Save successfully!`,
              type: "is-success",
            });
          },
        });
      } catch (error) {
        console.error(error);
        this.$buefy.dialog.confirm({
          title: "Message",
          message: `Something went wrong! Session may be expired.`,
          cancelText: "Close",
          confirmText: "Logout",
          type: "is-danger",
          trapFocus: true,
          onConfirm: () => {
            SACFrontendService.logout();
            this.$router.replace({ name: "TeacherLogin" });
          },
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.attendance-tracking {
  background-color: #fff;
  border: 3px solid #eee;
  min-width: 300px;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
}

.attendance {
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.attendance__header {
  flex-shrink: 0;
  padding: 0.5rem;
  border-bottom: 1px solid #eee;
}

.attendance__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.attendance__footer {
  flex-shrink: 0;
  padding: 0.5rem;
  border-top: 1px solid #eee;
}

.footer-buttons {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.student-cell {
  padding: 0.25rem;
  font-size: 14px;
  vertical-align: middle;
}

.student-name-container {
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: flex-start;
}

.student-name {
  cursor: pointer;
  text-align: left;
  /* wrap at most 2 lines */
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  transition: background-color 0.2s ease;
}

.student-name:hover {
  background-color: #f0f0f0;
}

.score-button {
  cursor: pointer;
  font-size: 1rem;
  line-height: 1rem;
  width: 1rem;
  padding: 0 0.125rem;
  background-color: #fff;
  /* on hover transition background color */
  transition: background-color 0.2s ease;
}

.score-button:hover {
  background-color: #ccc;
}
</style>
